import { KeyboardEvent, useRef, useState } from 'react';
import { Keyboard } from 'lucide-react';
import { SCREEN_TYPES } from 'utils/constants';
import { processTrackingId } from 'utils/tracking';
import { useAppStore, useReceiptStore, useTrackingStore } from 'store';
import { useProductStore } from 'store/productStore';
import { toast } from 'sonner';
import { Button } from 'components/ui/Button';
import { CardTitle } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { useKeyboardInputToggle } from 'hooks/useKeyboardInputToggle';
import { getBarcodeAction, isInputBarcode } from 'services/barcodes';
import { getFedexId, scanId } from 'services/barcodes/checkFedexId';
import { BarcodeType } from 'types';
import { handleCloseReceipt } from '../utils';

export default function ScanShippingBox() {
  const [trackingId, setTrackingId] = useState('');
  const [loading, setLoading] = useState(false);
  const trackingInputRef = useRef<HTMLInputElement>(null);
  const { keyboardVisible, toggleKeyboardInput } = useKeyboardInputToggle(trackingInputRef);
  const { setCurrentScreen, setBarcodeDebugMessage } = useAppStore();
  const { receiptData: cachedReceiptData } = useReceiptStore();
  const { lastTrackingId } = useTrackingStore();
  const { setProductNew, setProductCondition } = useProductStore();

  const handleCloseReceiptClick = (): void => {
    // Check if items were scanned (receipt_ind='y')
    console.log('ConfirmClose check - receiptData:', cachedReceiptData);
    console.log('Lines:', cachedReceiptData?.lines);
    const hasScannedItems = cachedReceiptData?.lines?.some((line) => {
      console.log('Line receipt_ind:', line?.receipt_ind);
      return line?.receipt_ind === 'y';
    });
    console.log('hasScannedItems:', hasScannedItems);
    if (hasScannedItems) {
      // Show ConfirmClose page only if items were scanned
      setCurrentScreen(SCREEN_TYPES.CONFIRM_CLOSE);
    } else {
      // Close directly if no items scanned
      handleCloseReceipt();
    }
  };

  const handleSubmit = async (): Promise<void> => {
    const trimmedTrackingId = trackingId.trim();

    if (!trimmedTrackingId) {
      toast.warning('Please enter tracking number');
      return;
    }

    // Check for FedEx patterns
    const fedexId = getFedexId(trimmedTrackingId);
    if (fedexId !== trimmedTrackingId) {
      setBarcodeDebugMessage(`FedEx ID detected: ${fedexId}`);
      setTrackingId(fedexId);
      await scanId(trimmedTrackingId);
      return;
    }

    // Check for other barcode patterns
    const barcodeType = isInputBarcode(trimmedTrackingId) as BarcodeType | null;
    if (barcodeType) {
      setBarcodeDebugMessage(`Barcode detected: ${barcodeType}`);
      getBarcodeAction(barcodeType, SCREEN_TYPES.SCAN_BOX, {
        setProductNew,
        setProductCondition,
        setBarcodeDebugMessage,
      });
      setTrackingId('');
      return;
    }

    // Process as regular tracking ID
    await processTrackingId({
      trackingId: trimmedTrackingId,
      source: 'manual',
      onLoading: setLoading,
    });
    
    // Clear input only on success (processTrackingId will handle loading state)
    // If successful, the screen will change to SCAN_ITEM
    // If failed, user stays on this screen with the input intact for correction
  };

  return (
    <div className="space-y-4 sm:space-y-5">
      <CardTitle className="mb-1 flex items-center justify-center gap-3 text-center text-xl font-extrabold tracking-tight text-slate-900 sm:mb-2 sm:text-2xl">
        Scan Shipping Box
      </CardTitle>

      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
        <Label htmlFor="tracking-id" className="mb-2 block text-sm font-semibold text-slate-700">
          Tracking #
        </Label>
        <div className="flex items-center gap-2.5">
          <Input
            ref={trackingInputRef}
            id="tracking-id"
            type="text"
            inputMode={keyboardVisible ? 'text' : 'none'}
            placeholder="Input field"
            value={trackingId}
            onChange={(e) => setTrackingId(e.target.value)}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => e.key === 'Enter' && handleSubmit()}
            disabled={loading}
            autoFocus
            className="text-base"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-12 w-12 shrink-0 rounded-xl border-slate-300"
            onClick={toggleKeyboardInput}
            aria-pressed={keyboardVisible}
          >
            <Keyboard className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
        <span className="mr-2 text-sm font-semibold text-slate-600 sm:text-base">
          Previous Tracking #
        </span>
        <span className="break-all text-sm font-semibold text-slate-900 sm:text-base">
          {lastTrackingId || ''}
        </span>
      </div>

      <Button
        onClick={handleCloseReceiptClick}
        disabled={!lastTrackingId}
        className="h-12 w-full rounded-xl bg-emerald-600 text-base font-semibold text-white shadow-sm hover:bg-emerald-700"
      >
        CLOSE RECEIPT
      </Button>
    </div>
  );
}
